import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Building2,
  FileWarning,
  Globe2,
  ListChecks,
  Network,
  Scale,
  ShieldCheck,
} from "lucide-react";

import { TRDRFooter } from "@/components/layout/trdr-footer";
import { TRDRHeader } from "@/components/layout/trdr-header";
import { Button } from "@/components/ui/button";

const earlyAccessHref =
  "mailto:hello@trdrhub.com?subject=Trade%20Audit%20Early%20Access";

const reviewAreas = [
  { icon: ListChecks, label: "Classification and product controls" },
  { icon: Scale, label: "Customs valuation and related-party treatment" },
  { icon: Globe2, label: "Origin, preference, and supplier evidence" },
  { icon: ShieldCheck, label: "Licensing, sanctions, and restricted trade" },
  { icon: FileWarning, label: "Document and recordkeeping controls" },
  { icon: Building2, label: "Roles, approvals, and governance" },
  { icon: Network, label: "Recurring process and system failures" },
];

const outputs = [
  "Risk map by issue, jurisdiction, business unit, and control",
  "Evidence-linked transaction findings",
  "Financial and regulatory exposure assessment",
  "Control-gap and root-cause analysis",
  "Prioritized remediation roadmap with owners and target dates",
];

export default function TradeAuditLanding() {
  return (
    <div className="min-h-screen bg-[#00261C]">
      <TRDRHeader />
      <main>
        <section className="relative overflow-hidden border-b border-[#EDF5F2]/10 pb-20 pt-36 md:pt-44">
          <div className="pointer-events-none absolute left-1/2 top-0 h-[540px] w-[760px] -translate-x-1/2 rounded-full bg-amber-300/10 blur-[150px]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(178,242,115,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(178,242,115,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_75%)]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-5xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/10 px-4 py-2">
                <BookOpenCheck className="h-4 w-4 text-amber-200" />
                <span className="font-mono text-xs uppercase tracking-[0.14em] text-amber-200">
                  Coming Soon
                </span>
              </div>
              <h1 className="mt-7 font-display text-5xl font-bold tracking-tight text-white sm:text-6xl">
                Trade Audit
              </h1>
              <p className="mx-auto mt-6 max-w-3xl text-xl leading-relaxed text-[#EDF5F2]/70">
                Find transaction risks and the company controls that allow them to repeat.
              </p>
              <p className="mx-auto mt-5 max-w-3xl leading-relaxed text-[#EDF5F2]/50">
                Trade Audit examines transactions, evidence, systems, and governance across a
                defined business scope. The delivery connects individual findings to financial
                exposure, control failures, and practical remediation.
              </p>
              <Button
                asChild
                size="lg"
                className="mt-9 h-auto border-none bg-[#B2F273] px-8 py-5 font-bold text-[#00261C] hover:bg-[#a3e662]"
              >
                <a href={earlyAccessHref}>
                  Join early access
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="border-b border-[#EDF5F2]/10 py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[.8fr_1.2fr] lg:items-center">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                  Entry engagement
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                  Start with a Trade Risk Scan.
                </h2>
                <p className="mt-5 leading-relaxed text-[#EDF5F2]/60">
                  Establish the risk pattern before committing to a wider institutional audit.
                </p>
              </div>
              <div className="rounded-2xl border border-[#B2F273]/20 bg-[#00382E]/60 p-8">
                <p className="text-lg leading-relaxed text-[#EDF5F2]/70">
                  The launch scope covers <strong className="text-white">one business unit</strong>,{" "}
                  <strong className="text-white">one jurisdiction</strong>,{" "}
                  <strong className="text-white">up to 2,500 transactions</strong>, and{" "}
                  <strong className="text-white">one year of activity</strong>.
                </p>
                <div className="mt-6 border-t border-[#EDF5F2]/10 pt-6">
                  <p className="font-display text-2xl font-bold text-white">
                    Trade Risk Scan from $10,000
                  </p>
                  <p className="mt-2 text-sm text-[#EDF5F2]/50">
                    The scan defines the evidence gaps, risk concentration, and scope for a full
                    audit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#EDF5F2]/10 bg-[#EDF5F2] py-20 text-[#00261C]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="max-w-3xl">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#315f50]">
                  Review areas
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">
                  Test the transactions and the controls behind them.
                </h2>
              </div>
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {reviewAreas.map(({ icon: Icon, label }) => (
                  <article
                    key={label}
                    className="flex items-start gap-4 rounded-xl border border-[#00261C]/10 bg-white/38 p-5"
                  >
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#315f50]" />
                    <h3 className="font-display text-sm font-semibold">{label}</h3>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#EDF5F2]/10 py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                  Delivery
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold text-white">
                  A report that leads to action.
                </h2>
                <ul className="mt-7 space-y-3">
                  {outputs.map((output) => (
                    <li key={output} className="flex items-start gap-3 text-[#EDF5F2]/60">
                      <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#B2F273]" />
                      {output}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-[#EDF5F2]/10 bg-[#00382E]/60 p-8">
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-[#B2F273]">
                  Different from Duty Audit
                </p>
                <h2 className="mt-4 font-display text-2xl font-bold text-white">
                  Duty treatment is one part of the review.
                </h2>
                <p className="mt-4 leading-relaxed text-[#EDF5F2]/60">
                  Duty Audit focuses on customs entries, supported recovery, and customs
                  exposure. Trade Audit examines the wider trade compliance system, including
                  governance, licensing, sanctions, evidence controls, and recurring process
                  failures.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 text-center">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl rounded-2xl border border-[#B2F273]/20 bg-[#00382E]/60 p-8 sm:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
                Commercial scope
              </p>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <div className="rounded-xl border border-[#EDF5F2]/10 bg-[#00261C]/50 p-6">
                  <p className="font-display text-2xl font-bold text-white">
                    Trade Risk Scan from $10,000
                  </p>
                  <p className="mt-2 text-sm text-[#EDF5F2]/50">
                    One defined scope used to locate and prioritize risk.
                  </p>
                </div>
                <div className="rounded-xl border border-[#EDF5F2]/10 bg-[#00261C]/50 p-6">
                  <p className="font-display text-2xl font-bold text-white">
                    Full Trade Audit from $25,000
                  </p>
                  <p className="mt-2 text-sm text-[#EDF5F2]/50">
                    Final pricing depends on jurisdictions, transaction volume, and assurance
                    responsibility.
                  </p>
                </div>
              </div>
              <Button
                asChild
                size="lg"
                className="mt-8 h-auto border-none bg-[#B2F273] px-8 py-5 font-bold text-[#00261C] hover:bg-[#a3e662]"
              >
                <a href={earlyAccessHref}>Join early access</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <TRDRFooter />
    </div>
  );
}
